import React, {useState, useEffect, useRef} from 'react'
import {useNavigate} from 'react-router-dom'
import {ListGroup, Row, Col, InputGroup, Form, Button} from 'react-bootstrap';
import {MdSearch} from 'react-icons/md'
import {FiDatabase, FiChevronLeft, FiChevronRight, FiChevronsLeft, FiChevronsRight} from 'react-icons/fi'
import {isMobile} from 'react-device-detect';
import useKey from "@rooks/use-key";
import {ButtonNew} from './buttons'
import { BusSpinner } from './BusSpinner'

/**
 *    * onFetch -
 *    * cols
 *    * onSelect
 *    * boolean search = Mostrar la búsqueda
 *    * controls
 *    * rows
 *    * page
 *    * pages
 *    * count
 *    * title
 *    * className
 *    * showHeaders = mostrar las cabeceras del listado. Por defecto es true.
 */
export default function ZList(
    {
        onFetch,
        cols,
        onSelect,
        search,
        controls,
        rows,
        page,
        pages,
        count,
        title,
        className,
        showHeaders = true,
        onCreate
    }
) {

    const navigate = useNavigate();
    const [searchingText, setSearchingText] = useState('');
    const [loading, setLoading] = useState(false);

    // Reference to track if component is mounted
    const isMounted = useRef(true);

    // Cleanup function to prevent state updates after unmount
    useEffect(() => {
        return () => {
            isMounted.current = false;
        };
    }, []);

    const loadData = async (params = {}) => {
        setLoading(true);
        try {
            await onFetch(params);
        } catch (error) {
            console.error('Error fetching data:', error);
        }

        // Only update state if component is still mounted
        if (isMounted.current) {
            setLoading(false);
        }
    };

    // -----| Handle form |-----
    const handleForm = (ev) => {
        setSearchingText(ev.target.value);
    }

    // Handle Enter key press for search
    useKey(["Enter"], () => {
        if (search && searchingText !== undefined) {
            loadData({search: searchingText});
        }
    }, {
        // Add options to prevent memory leaks
        when: search && searchingText !== undefined
    });

    // -----| Render Header |-----
    const renderHeader = (col, idx) => {

        const cls = ['py-2'];

        // Align
        switch (col.align && col.align.toUpperCase()) {
            case 'L':
                cls.push('text-start');
                break;
            case 'R':
                cls.push('text-end');
                break;
            default:
                cls.push('text-center');
                break;
        }

        return (
            <Col key={idx} md={col.md} className={cls.join(' ')}>
                <span style={{
                    fontWeight: 600,
                    fontSize: '0.85rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    color: '#667eea'
                }}>
                    {col.caption}
                </span>
            </Col>
        )
    }

    /**
     * Render cell
     */
    const renderCell = (row, col, idx) => {

        if (col.component) {

            let comp;
            if (col.component instanceof Function) comp = col.component(row);
            else comp = col.component
            return <Col key={idx} md={col.md}>{comp}</Col>

        } else {
            let val;
            const cls = ['py-1'];
            if (col.value && col.value instanceof Function) val = col.value(row);
            else val = row[col.field];

            // Align
            switch (col.align && col.align.toUpperCase()) {
                case 'L':
                    cls.push('text-start');
                    break;
                case 'R':
                    cls.push('text-end');
                    break;
                default:
                    cls.push('text-center');
                    break;
            }

            // Render
            if (isMobile) {
                return (
                    <Col key={idx} md={col.md} xs={col.xs} className="text-start py-1">
                        <span className="d-inline d-sm-none text-muted small">{col.caption}: </span>
                        {val}
                    </Col>
                )

            } else {
                return (
                    <Col key={idx} md={col.md} className={cls.join(' ')}>{val}</Col>
                )
            }
        }
    }

    // Pagination button style
    const paginationBtnStyle = (disabled) => ({
        width: '36px',
        height: '36px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '8px',
        border: 'none',
        background: disabled ? '#f0f0f0' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: disabled ? '#999' : '#fff',
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'all 0.2s ease',
        opacity: disabled ? 0.5 : 1
    });

    return (
        <div className={className}>

            {title && <div className="mb-3"><h5 style={{ color: '#333', fontWeight: 600 }}>{title}</h5></div>}

            {/* Search and Pagination Row */}
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">
                {/* Search */}
                <div style={{ minWidth: '280px', flex: '1', maxWidth: '400px' }}>
                    {search && (
                        <InputGroup style={{
                            borderRadius: '10px',
                            overflow: 'hidden',
                            boxShadow: '0 2px 8px rgba(102, 126, 234, 0.15)'
                        }}>
                            <Form.Control
                                name="text"
                                value={searchingText}
                                onChange={handleForm}
                                placeholder="Buscar..."
                                style={{
                                    border: '2px solid #e9ecef',
                                    borderRight: 'none',
                                    padding: '10px 16px',
                                    fontSize: '0.95rem'
                                }}
                            />
                            <Button
                                onClick={() => loadData({search: searchingText})}
                                style={{
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    border: 'none',
                                    padding: '10px 16px'
                                }}
                            >
                                <MdSearch size={20} />
                            </Button>
                        </InputGroup>
                    )}
                    {controls}
                </div>

                {/* Create button */}
                {onCreate && (
                    <div>
                        <ButtonNew onClick={() => onCreate ? onCreate() : navigate("/user/create")} />
                    </div>
                )}

                {/* Pagination */}
                {page && (
                    <div className="d-flex align-items-center gap-3">
                        <span className="text-muted d-flex align-items-center" style={{ fontSize: '0.9rem' }}>
                            <FiDatabase className="me-2" style={{ color: '#667eea' }} />
                            <strong style={{ color: '#667eea' }}>{count}</strong>
                            <span className="ms-1">elementos</span>
                        </span>

                        <div className="d-flex align-items-center gap-2">
                            <button
                                style={paginationBtnStyle(page <= 1)}
                                onClick={() => loadData({page: 1})}
                                disabled={page <= 1}
                                title="Primera página"
                            >
                                <FiChevronsLeft size={18} />
                            </button>
                            <button
                                style={paginationBtnStyle(page <= 1)}
                                onClick={() => loadData({page: page - 1})}
                                disabled={page <= 1}
                                title="Anterior"
                            >
                                <FiChevronLeft size={18} />
                            </button>

                            <div
                                style={{
                                    padding: '8px 16px',
                                    background: '#f8f9fa',
                                    borderRadius: '8px',
                                    fontWeight: 500,
                                    color: '#333',
                                    minWidth: '100px',
                                    textAlign: 'center'
                                }}
                            >
                                <span style={{ color: '#667eea', fontWeight: 600 }}>{page}</span>
                                <span className="text-muted mx-1">de</span>
                                <span style={{ fontWeight: 600 }}>{pages || 1}</span>
                            </div>

                            <button
                                style={paginationBtnStyle(page >= pages || !pages)}
                                onClick={() => loadData({page: page + 1})}
                                disabled={page >= pages || !pages}
                                title="Siguiente"
                            >
                                <FiChevronRight size={18} />
                            </button>
                            <button
                                style={paginationBtnStyle(page >= pages || !pages)}
                                onClick={() => loadData({page: pages})}
                                disabled={page >= pages || !pages}
                                title="Última página"
                            >
                                <FiChevronsRight size={18} />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Table */}
            <div style={{
                borderRadius: '12px',
                overflow: 'hidden',
                border: '1px solid #e9ecef'
            }}>
                <ListGroup variant="flush">
                    {/* Header */}
                    {showHeaders && (
                        <ListGroup.Item
                            className="d-none d-sm-block"
                            style={{
                                background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                                borderBottom: '2px solid #e9ecef'
                            }}
                        >
                            <Row className="align-items-center">
                                {cols && cols.map((col, idx) => renderHeader(col, idx))}
                            </Row>
                        </ListGroup.Item>
                    )}

                    {/* Rows */}
                    {rows && rows.map((row, idxRow) => (
                        <ListGroup.Item
                            key={idxRow}
                            action
                            onClick={() => onSelect ? onSelect(row) : null}
                            style={{
                                borderLeft: '3px solid transparent',
                                transition: 'all 0.2s ease',
                                padding: '12px 16px'
                            }}
                            className="zlist-row"
                        >
                            <Row className="align-items-center">
                                {cols && cols.map((col, idxCol) => renderCell(row, col, idxCol))}
                            </Row>
                        </ListGroup.Item>
                    ))}

                    {/* Loading */}
                    {loading && (
                        <ListGroup.Item className="border-0 py-4">
                            <BusSpinner />
                        </ListGroup.Item>
                    )}

                    {/* Empty state */}
                    {!loading && (!rows || rows.length === 0) && (
                        <ListGroup.Item
                            className="text-center py-5"
                            style={{ background: '#fafafa' }}
                        >
                            <FiDatabase size={48} className="mb-3" style={{ color: '#ddd' }} />
                            <div className="text-muted">No hay elementos para mostrar</div>
                        </ListGroup.Item>
                    )}
                </ListGroup>
            </div>

            {/* CSS for hover effects */}
            <style>{`
                .zlist-row:hover {
                    border-left-color: #667eea !important;
                    background: linear-gradient(90deg, rgba(102, 126, 234, 0.05) 0%, transparent 100%) !important;
                }
            `}</style>
        </div>
    )
}
